import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * HIPAA §164.312(a)(2)(iii) — automatic logoff after a period of inactivity.
 * Resets a 15-minute timer on any pointer/keyboard activity. When the timer
 * fires, calls `logout()` and redirects to /login. A 60s warning toast is
 * shown so the user can extend the session by moving the mouse.
 *
 * Mounted globally (in App.js) so it covers every authenticated page.
 */
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARN_AT_MS = 60 * 1000;           // 60s warning before lock

export default function IdleTimeoutProvider({ children }) {
  const { user, logout } = useAuth();
  const lastActivity = useRef(Date.now());
  const [warning, setWarning] = useState(false);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setWarning(false);
      if (tickRef.current) clearInterval(tickRef.current);
      return;
    }
    const onActivity = () => {
      lastActivity.current = Date.now();
      if (warning) setWarning(false);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    tickRef.current = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_TIMEOUT_MS) {
        try { logout(); } catch {}
        window.location.href = '/login?reason=idle';
      } else if (idle >= IDLE_TIMEOUT_MS - WARN_AT_MS) {
        setWarning(true);
      }
    }, 5000);

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [user, logout, warning]);

  return (
    <>
      {children}
      {warning && user && (
        <div
          data-testid="idle-warning"
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
            backgroundColor: '#1a1a2e', color: '#FFF', padding: '14px 18px',
            borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            border: '1px solid #B22222', maxWidth: 320, fontSize: 13,
          }}
        >
          <strong style={{ display: 'block', marginBottom: 4 }}>Session about to expire</strong>
          You'll be signed out in under a minute due to inactivity. Move the mouse to stay signed in.
        </div>
      )}
    </>
  );
}
